import { FormEvent, useEffect, useState } from 'react';
import {
  MemberCustomerServiceCategory,
  MemberCustomerServiceActivity,
  MemberCustomerServiceSourceType,
  MemberCustomerServiceTicket,
  createMemberCustomerServiceTicket,
  getMemberCustomerServiceTicket,
  listMemberCustomerServiceTickets,
  memberCustomerServiceStatusClass,
  memberCustomerServiceStatusLabels,
} from '@/services/memberCustomerService';

export type AccountSupportTarget = {
  sourceType: MemberCustomerServiceSourceType;
  sourceId?: string | null;
  title: string;
  description?: string;
  category: MemberCustomerServiceCategory;
};

export default function AccountSupportDialog({ target, onClose }: { target: AccountSupportTarget | null; onClose: () => void }) {
  const [tickets, setTickets] = useState<MemberCustomerServiceTicket[]>([]);
  const [activeTicket, setActiveTicket] = useState<MemberCustomerServiceTicket | null>(null);
  const [activities, setActivities] = useState<MemberCustomerServiceActivity[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!target) return;
    setSubject(target.title);
    setMessage('');
    setError('');
    setActiveTicket(null);
    setActivities([]);
    setLoading(true);
    listMemberCustomerServiceTickets(target.sourceType, target.sourceId || undefined)
      .then(setTickets)
      .catch((err) => setError(err instanceof Error ? err.message : '客服紀錄讀取失敗。'))
      .finally(() => setLoading(false));
  }, [target]);

  if (!target) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!target || !message.trim()) return;
    setSaving(true);
    setError('');
    try {
      const ticket = await createMemberCustomerServiceTicket({
        SourceType: target.sourceType,
        SourceId: target.sourceId || null,
        Subject: subject.trim() || target.title,
        Category: target.category,
        Message: message.trim(),
      });
      setTickets((current) => [ticket, ...current]);
      setMessage('');
      await openTicket(ticket);
    } catch (err) {
      setError(err instanceof Error ? err.message : '客服問題送出失敗。');
    } finally {
      setSaving(false);
    }
  }

  async function openTicket(ticket: MemberCustomerServiceTicket) {
    setActiveTicket(ticket);
    setActivities([]);
    setDetailLoading(true);
    setError('');
    try {
      const data = await getMemberCustomerServiceTicket(ticket.TicketId);
      setActiveTicket(data.Ticket);
      setActivities(data.Activities);
    } catch (err) {
      setError(err instanceof Error ? err.message : '客服詳情讀取失敗。');
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-[#10251c]/45 px-0 sm:items-center sm:px-4">
      <button aria-label="關閉客服視窗" className="absolute inset-0 cursor-default" onClick={onClose} type="button" />
      <section className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        <div className="border-b border-[#E8E0D6] px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-[#9A8A7A]">客服協助</p>
              <h3 className="mt-1 text-xl font-bold text-[#1B3D2A]">{target.title}</h3>
              {target.description && <p className="mt-1 text-sm text-[#5A6B5A]">{target.description}</p>}
            </div>
            <button className="rounded-full border border-[#E8E0D6] px-3 py-1.5 text-sm font-bold text-[#5A6B5A]" onClick={onClose} type="button">
              關閉
            </button>
          </div>
        </div>

        <div className="grid gap-5 overflow-y-auto px-5 py-5">
          {error && <div className="rounded-xl border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{error}</div>}

          <form className="grid gap-3 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-4" onSubmit={submit}>
            <label className="grid gap-1 text-sm font-bold text-[#1B3D2A]">
              問題主旨
              <input
                className="h-11 rounded-xl border border-[#E8E0D6] bg-white px-3 text-sm outline-none focus:border-[#1B3D2A] focus:ring-2 focus:ring-[#1B3D2A]/10"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </label>
            <label className="grid gap-1 text-sm font-bold text-[#1B3D2A]">
              問題內容
              <textarea
                className="min-h-[120px] rounded-xl border border-[#E8E0D6] bg-white px-3 py-2 text-sm outline-none focus:border-[#1B3D2A] focus:ring-2 focus:ring-[#1B3D2A]/10"
                placeholder="請描述需要協助的地方，例如想改預約時間、詢問配送進度、付款問題等。"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-[#9A8A7A]">送出後客服會在後台接續處理，狀態會同步回會員中心。</p>
              <button className="h-11 rounded-xl bg-[#1B3D2A] px-5 text-sm font-bold text-white disabled:opacity-60" disabled={saving || !message.trim()} type="submit">
                {saving ? '送出中...' : '送出問題'}
              </button>
            </div>
          </form>

          <section>
            <h4 className="mb-3 text-sm font-bold text-[#1B3D2A]">這筆相關客服紀錄</h4>
            {loading ? (
              <div className="rounded-2xl border border-[#E8E0D6] py-8 text-center text-sm text-[#9A8A7A]">讀取中...</div>
            ) : tickets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#E8E0D6] py-8 text-center text-sm text-[#9A8A7A]">尚無客服紀錄。</div>
            ) : (
              <div className="grid gap-3">
                {tickets.map((ticket) => (
                  <button className={`rounded-2xl border bg-white p-4 text-left ${activeTicket?.TicketId === ticket.TicketId ? 'border-[#1B3D2A]' : 'border-[#E8E0D6]'}`} key={ticket.TicketId} onClick={() => openTicket(ticket)} type="button">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-[#1B3D2A]">{ticket.Subject}</p>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${memberCustomerServiceStatusClass[ticket.Status]}`}>
                        {memberCustomerServiceStatusLabels[ticket.Status]}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#9A8A7A]">{ticket.TicketNumber} / 更新於 {ticket.UpdatedAt || '-'}</p>
                  </button>
                ))}
              </div>
            )}
          </section>

          {activeTicket && (
            <section className="rounded-2xl border border-[#E8E0D6] bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="text-sm font-bold text-[#1B3D2A]">{activeTicket.Subject}</h4>
                  <p className="mt-1 text-xs text-[#9A8A7A]">{activeTicket.TicketNumber}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${memberCustomerServiceStatusClass[activeTicket.Status]}`}>
                  {memberCustomerServiceStatusLabels[activeTicket.Status]}
                </span>
              </div>
              <div className="mt-4 grid gap-3">
                {detailLoading ? (
                  <p className="rounded-xl bg-[#FAF8F5] px-4 py-6 text-center text-sm text-[#9A8A7A]">讀取詳情中...</p>
                ) : activities.length === 0 ? (
                  <p className="rounded-xl bg-[#FAF8F5] px-4 py-6 text-center text-sm text-[#9A8A7A]">目前沒有可查看的對話內容。</p>
                ) : activities.map((activity) => (
                  <div className={`rounded-2xl px-4 py-3 ${activity.ActivityType === 'Customer' ? 'bg-[#FAF8F5]' : 'bg-[#e9f4ec]'}`} key={activity.ActivityId}>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#9A8A7A]">
                      <span className="font-bold text-[#1B3D2A]">{activity.ActivityType === 'Customer' ? '我的提問' : activity.AuthorName}</span>
                      <span>{activity.CreatedAt || '-'}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#1B3D2A]">{activity.Body}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
}
